//===============================================================================
// © 2017 eWorkplace Apps.  All rights reserved.
// Original Author: Sanjeev Khanna
// Original Date: 28 June 2017
//==============================================================================

import { Component } from '@angular/core';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

/**  
  * This class provides Parsing/formatting/validating phone numbers for all countries/regions of the world
  * Default region/country code.
  * 
  * PhoneNumber stored in following syntax, our canonical form:
  * [+<digits>]<digits>[,<digits>]
  * The first part, optional, is for country code with leading '+'.country
  * The second part is the complete phone number with all digits
  * The third part, option, is for extension with leading comma.
  *
  * When the country code is missing, use the default region/country code.
  * NEED TO DISCUSS ABOVE. Applied in the client, also defaulted in the server?
  *
  * Regular expression for canonical form:
  * ^(\+[0-9]*)?([0-9]*)$
  *
  * The phone number may be entered with spaces, dashes and brackets and strings like "ext" or "x". We ignore ALL that and reduce it to the canonical form before storing it.
  *
  * The phone number is ALWAYS displayed in the local style of the region (country code).
  *
  * In view mode the display style for the extension is:  "ext <digits>".
  * In Edit mode, the display style for the extension is ",<digits>".
  *
  * TODO:
  * Allow multiple commas (contiguous)
  * Allow other meta characters (check iPhone number dialer keyboard)

  * Skip all characters up to first "+" or digit.
  * Collect all characters that are '+' (only first), digits, comma (only first). Skip all other
  * charactes. If no digits after comma, skip the comma also.
  * OR USE GOOGLE API to first clean it up, and skip all characters other than plus, digit and comma.  
  */
export class PhoneNumberHelper {
    private canonicalNumber: string;
    private defaultRegion: string;
    private defaultCountyCode: string;

    /**     
     *   Gets or sets the country code of a phone number.     
     *   @value:: Country code of phone number.   
     *   @remarks:: If phone number does not contain country code then it returns default country code.
     */
    public countryCode: string

    /**     
     *   Gets or sets the national phone number.
     *   @value:: National phone number.     
     */
    public nationalPhoneNumber: string

    /**     
     *   Gets or sets the number of pauses(',') in phone number.
     *   @value:: Number of pauses.     
     */
    public numberOfPauses: number

    /**     
     *   Gets or sets the extension number.
     *   @value:: Extension number.
     */
    public extensionNumber: string

    /**     
     *   Gets the phone number by other properties in specific format '+19999999999,,,,333'.
     *   @value:: Phone number in following format '+19999999999,,,,333'.
     */    
    public phoneNumber(): string {
        return this.getPhoneNumber();
    }

    /**     
     *   Gets the default region code of phone number.
     *   @value:: Default region code of phone number.
     */        
    public defaultCountryCode(): string {
        return this.defaultCountyCode;
    }

    /**     
     *   Initializes a new instance of the <see cref="PhoneNumberHelper"/> class.
     *   @value:: Default region code of phone number.
     *   @param:: name="defaultRegion" :: User's default region, for ex.'US'.
     *   @param:: name="phoneNumber" :: Phone number.
     */            
    public constructor(defaultCountryCode: string, phoneNumber: string) {
        let intDefaultCountryCode: number = 1;
        let _defaultRegion: string = "ÜS";
        
        if (isNaN(Number(defaultCountryCode.replace('+', ''))) == false) {
            intDefaultCountryCode = Number(defaultCountryCode.replace('+', ''));
        }

        this.defaultCountyCode = "+" + intDefaultCountryCode;

        // Set default region and country code.
        this.setRegionAndCountry(intDefaultCountryCode);

        // Parse phone number and fill properties.
        this.parse(phoneNumber);
    }

    /**     
     *   Evalutes the country code from provided region using Google API.
     *   @return:: Return country code for provided region code     
     */           
    public static countryCodeFromRegion(region: string): string {
        // Get country code from given region.            
        return PhoneNumberUtil.getInstance().getCountryCodeForRegion(region);
    }

    /**     
     *   Method is used to convert raw number to cannonical form.
     *   It will remove non numeric and special charecter from raw phone#.
     *   It allows:
     *   Single "+" at starting.
     *   0-9 digits.
     *   No. of pauses if extention # provided. 
     *   @param name="phoneNumber" ::  The raw number.
     *   @return:: Return canonical number.
     */               
    public static normalize(phoneNumber: string, defaultCountryCode: number): string {            
        // Get the number from first occurance of + sign.
        let range: number = phoneNumber.indexOf('+');

        if (range >= 0) {
            phoneNumber = phoneNumber.substring(range);
        }

        let plusAppear: boolean = false;
        let number: string = "";
        let charsLength: number = phoneNumber.length - 1;
        let rawNumberChars = phoneNumber.split('');
        var i: number
        for (i = 0; i <= charsLength; i++) {
            if (rawNumberChars[i].toString() == "+") {
                number = plusAppear ? number : rawNumberChars[i].toString();
                plusAppear = true;
                continue;
            }

            if (rawNumberChars[i].toString() == ",") {

                // Add comma, if comma appear in between of phone#. otherwise ignore.
                if (i == 0 || i == charsLength) {
                    continue;
                }

                var j: number;

                // Ignore if user type the comma in contineous like 9898989898,,,,,,,,
                for (j = i + 1; j <= charsLength; j++) {
                    // if comma appear till the end then ignore comma to add.
                    if (rawNumberChars[j].toString() == "," && j == charsLength) {
                        continue;
                    }
                }

                // Add comma, if comma appear in between of phone#.
                number = number + phoneNumber[i];
                continue;
            }

            // TODO: Check for other language numeric values.                
            //  Ignoer all special charecters.
            if (isNaN(Number(rawNumberChars[i])) == false) {
                number +=  phoneNumber[i].trim();
            }
        }

        if (number == "+" || number == "," || number == "+,") {
            return "";

        }


        // Check that phone number has country code.
        if (!plusAppear)
            number = "+" + defaultCountryCode + number;

        return number;
    }

    /**     
     *   Parses the specified phone number and map it to properties.     
     *   @param name="phoneNumber" ::  Raw phone number.     
     */                   
    public parse(phoneNumber: string) {
        try {

            // Normalize given phone number.
            this.canonicalNumber = PhoneNumberHelper.normalize(phoneNumber, Number(this.defaultCountyCode));

            // Parse normalized phone number and assign into properties.
            var number = PhoneNumberUtil.getInstance().parse(this.canonicalNumber, this.defaultRegion);

            // Check that phone number has country code.
            if (number.hasCountryCode)
                this.countryCode = number.getCountryCodeOrDefault();

            // Check that phone number has 'National Phone' no.
            if (number.hasNationalNumber) {
                this.nationalPhoneNumber = number.getNationalNumberOrDefault();

                // Check that phone number has extension number then map extension number and calculate number of comma in between.
                if (number.hasExtension) {
                    this.extensionNumber = number.getExtensionOrDefault();

                    // Calculate number of pauses (',') between national number and extention number.
                    let startFrom: number = this.canonicalNumber.indexOf(this.nationalPhoneNumber) + this.nationalPhoneNumber.toString().length;
                    let endTo: number = this.canonicalNumber.lastIndexOf(this.extensionNumber);
                    this.numberOfPauses = this.canonicalNumber.substring(startFrom, endTo - startFrom).split(",").toString().length - 1;
                }
            }
        }
        // Ignore if phone api doesn't parse the number
        catch (Exception) {
        }
    }

    /**     
     *   Call Google API to format in the local style of the country code.
     *   @return ::  Formated phone number.     
     */                       
    public format(): string {
        try {
            // Check that canonicalNumber is not null or empty if empty then 
            if (this.isEmpty(this.phoneNumber()))
                throw new Error("Phone number is either blank or invalid.");

            // Initialize phone util
            let phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();

            // Parse canonical phone number
            var phoneNumber = phoneUtil.parseAndKeepRawInput(this.phoneNumber(), this.defaultRegion);

            // Format phone number in International format.
            return phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL);
        }
        // Return canonical number if phone api doesn't format the number.
        catch (Exception) {
            return this.canonicalNumber;
        }
    }

    /**     
     *   Determines whether [is international number].
     *   @return :: True if CountryCode is null/blank OR CountryCode is not equal to DefaultCountryCode.
     */                 
    public isInternationalNumber(): boolean {
        // Return True if CountryCode is null/blank OR CountryCode is not equal to DefaultCountryCode.
        if (this.isEmpty(this.countryCode) || (this.countryCode != this.defaultCountryCode())) {
            return true;
        }
        // Else return false.
        else {
            return false;
        }
    }


    /**     
     *   Sets the region and country.
     *   @param :: name="defaultRegionCountryCode" :: User's default region.
     */                     
    private setRegionAndCountry(defaultRegionCountryCode: number) {
        // Throw argument null exception if default country code is null or empty.
        if (defaultRegionCountryCode <= 0)
            throw new Error("DefaultCountryCode is required.");
        try {
            // Initialize default region and country code.          
            this.defaultRegion = PhoneNumberUtil.getInstance().getRegionCodeForCountryCode(defaultRegionCountryCode);
        }
        // Skip if country code not parse
        catch (Exception) {
        }
    }

    /**     
     *   Gets the phone number in following format '+19999999999,,,252'.     
     */                         
    private getPhoneNumber(): string {
        let phoneNumber: string = '';

        // Check that national phone number is not null or enmpty.
        if (!this.isEmpty(this.nationalPhoneNumber)) {
            // Check that country code is not null or empty then add it in phone number.
            if (!this.isEmpty(this.countryCode)) {
                phoneNumber = "+" + this.countryCode;
            }

            // Add national phone number in phone number property.
            phoneNumber = phoneNumber + this.nationalPhoneNumber;

            if (!this.isEmpty(this.extensionNumber)) {

                // Add Pauses(',') in phone number.
                for (let i: number = 0; i < this.numberOfPauses; i++) {
                    phoneNumber += ",";
                }

                // Check that extension number is not null or empty theen add it in phone number.
                phoneNumber += this.extensionNumber;
            }
        }
        return this.isEmpty(phoneNumber) ? this.canonicalNumber : phoneNumber;
    }

    /**     
     *   Function will check the empty value of parameter.     
     */                     
    public isEmpty(value: any) {
        if (value == null || value == 'undefined' || value == '')
            return true;
        else
            return false;
    }
}




// WEBPACK FOOTER //
// /Users/hrushabhmakwana/Documents/CAM/CreditRepairMenAngular/src/app/common/helpers/phone-number.helper.ts